import { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { roleLabel } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, Camera, Package, FilePlus2, BookMarked, Image as ImageIcon, Settings, LogOut, Menu, ArrowLeftRight, Sparkles,
} from "lucide-react";

const NAV = [
  { to: "/studio", label: "Studio Dashboard", icon: LayoutDashboard, end: true, testid: "snav-dashboard" },
  { to: "/studio/products/new", label: "Add Tea Product", icon: Camera, testid: "snav-add-product" },
  { to: "/studio/library", label: "Product Library", icon: Package, testid: "snav-library" },
  { to: "/studio/catalogues/new", label: "Create Catalogue", icon: FilePlus2, testid: "snav-create" },
  { to: "/studio/catalogues", label: "Saved Catalogues", icon: BookMarked, end: true, testid: "snav-catalogues" },
  { to: "/studio/covers", label: "Cover Page Library", icon: ImageIcon, testid: "snav-covers" },
  { to: "/studio/settings", label: "Settings", icon: Settings, testid: "snav-settings" },
];

function NavItems({ onNavigate }) {
  return (
    <nav className="flex flex-col gap-1 px-3">
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate} data-testid={n.testid}
          className={({ isActive }) => `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#AD8642] text-[#153E32] shadow-sm" : "text-[#F7F2E7]/80 hover:bg-white/10 hover:text-[#F7F2E7]"}`}>
          <n.icon className="h-4 w-4 shrink-0" />
          <span>{n.label}</span>
        </NavLink>
      ))}
      <Link to="/" data-testid="snav-switch-existing" className="mt-4 flex items-center gap-3 rounded-lg border border-[#AD8642]/40 px-3 py-2.5 text-sm text-[#F7F2E7]/85 hover:bg-white/10">
        <ArrowLeftRight className="h-4 w-4 shrink-0" />
        <span>Existing Catalogue Method</span>
      </Link>
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-3 px-6 py-6 border-b border-[#AD8642]/30">
      <div className="h-12 w-12 grid place-items-center shrink-0 rounded-full bg-[#303C29]">
        <img src="/pgt-logo-hd.png" alt="Prime Gold Tea" className="h-9 w-9 object-contain" data-testid="studio-sidebar-logo" />
      </div>
      <div className="leading-tight">
        <div className="font-display text-xl text-[#D6BC85] tracking-wide">Prime Gold Tea</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#F7F2E7]/70 flex items-center gap-1"><Sparkles className="h-3 w-3" /> AI Tea Catalogue Studio</div>
      </div>
    </div>
  );
}

export default function StudioLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex bg-[#FBFAF6]" data-testid="studio-layout">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-72 flex-col bg-[#153E32] z-40">
        <Brand />
        <div className="flex-1 overflow-y-auto py-4"><NavItems /></div>
        <div className="border-t border-[#AD8642]/30 p-4">
          <div className="mb-3 rounded-lg bg-white/5 px-3 py-2">
            <div className="text-sm font-semibold text-[#F7F2E7] truncate" data-testid="studio-sidebar-username">{user?.name}</div>
            <div className="text-[11px] text-[#D6BC85]">{roleLabel(user?.role)}</div>
          </div>
          <Button variant="ghost" onClick={doLogout} data-testid="studio-logout-btn" className="w-full justify-start gap-2 text-[#F7F2E7]/80 hover:bg-white/10 hover:text-[#F7F2E7]"><LogOut className="h-4 w-4" /> Sign out</Button>
        </div>
      </aside>
      <div className="lg:pl-72 flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between bg-[#153E32] px-4 py-3">
          <div className="flex items-center gap-2">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild><Button size="icon" variant="ghost" data-testid="studio-mobile-menu-btn" className="text-[#F7F2E7] hover:bg-white/10"><Menu className="h-5 w-5" /></Button></SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 bg-[#153E32] border-none">
                <Brand />
                <div className="py-4"><NavItems onNavigate={() => setOpen(false)} /></div>
                <div className="p-4 border-t border-[#AD8642]/30"><Button variant="ghost" onClick={doLogout} className="w-full justify-start gap-2 text-[#F7F2E7]/80 hover:bg-white/10"><LogOut className="h-4 w-4" /> Sign out</Button></div>
              </SheetContent>
            </Sheet>
            <span className="flex items-center gap-2"><img src="/pgt-logo-hd.png" alt="" className="h-8 w-8 object-contain" /><span className="font-display text-lg text-[#D6BC85]">AI Tea Catalogue Studio</span></span>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto"><Outlet /></main>
      </div>
    </div>
  );
}
